import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useFlags, useIdentity, useOrg, useRole, useUser } from '@/stores/identity';
import { useLocations } from '@/stores/locations';
import { useStaff } from '@/stores/staff';
import { useTimeOff } from '@/stores/timeOff';
import { getPlatformSettings } from '@/lib/admin';
import type { PlatformSettings } from '@/lib/types';
import { ActiveBanners } from '@/components/ActiveBanners';
import { PlanBadge } from '@/components/PlanBadge';
import { cn } from '@/lib/utils';

/**
 * AppShell — shared layout. Day 8: maintenance banner + audit/admin nav.
 * Day 9: ActiveBanners. Day 10: a11y polish (skip-to-main, focus rings).
 * Day 14.5: PharmaSync brand applied (variation B - 2x2 tile mark + two-tone wordmark)
 *           + brand amplification (header tint, org chip, navy stat values).
 *           Non-prod environment banner now lives in App.tsx (EnvBadge) so it
 *           also covers auth and legal pages, not just app shell.
 * Day 14.6: PlanBadge next to org chip + Billing nav tab (owner-only) +
 *           Pricing in footer.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const org = useOrg();
  const user = useUser();
  const role = useRole();
  const flags = useFlags();
  const isOwner = role === 'owner';
  const isAdmin = !!flags?.is_platform_admin;

  const [settings, setSettings] = useState<PlatformSettings | null>(null);

  useEffect(() => {
    let cancelled = false;
    getPlatformSettings()
      .then((s) => { if (!cancelled) setSettings(s); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  async function handleSignOut() {
    // Clear in-memory caches first so the UI immediately drops the previous
    // user's data — prevents a flash of stale orgs/locations/staff/time-off
    // before Supabase clears the session and redirects to sign-in.
    useLocations.getState().reset();
    useStaff.getState().reset();
    useTimeOff.getState().reset();
    useIdentity.getState()._reset();
    await supabase.auth.signOut();
  }

  // Audit F-13: the skip-to-main link needs an explicit focus call. Hash-only
  // anchor navigation scrolls the viewport but most browsers don't move
  // keyboard focus to the target, so the next Tab keeps going through the
  // header instead of starting inside <main>. tabIndex={-1} on <main> lets us
  // programmatically focus it without putting it in the tab order otherwise.
  const mainRef = useRef<HTMLElement>(null);
  function handleSkipToMain(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    const el = mainRef.current;
    if (!el) return;
    el.focus({ preventScroll: false });
  }

  return (
    <div className="min-h-screen bg-background">
      <a
        href="#main-content"
        onClick={handleSkipToMain}
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground"
      >
        Skip to main content
      </a>
      {settings?.maintenance_mode && (
        <div role="status" aria-live="polite" className="bg-red-600 text-white text-center text-sm py-2 px-4">
          <strong>Maintenance mode is active.</strong>{' '}
          {settings.maintenance_message ??
            'Mutations are temporarily disabled. Please try again shortly.'}
        </div>
      )}
      <ActiveBanners />
      <header
        className="border-b border-border"
        style={{ backgroundColor: 'hsl(var(--brand-sky) / 0.18)' }}
      >
        <div className="container mx-auto flex items-center justify-between py-4 gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={org?.display_name ? `PharmaSync home - ${org.display_name}` : 'PharmaSync home'}
            >
              <img src="/logo.svg" alt="" className="h-9 w-9" aria-hidden="true" />
              <span className="space-y-1">
                <h1 className="text-lg font-semibold tracking-tight leading-none">
                  <span style={{ color: 'hsl(var(--brand-navy))' }}>Pharma</span>
                  <span style={{ color: 'hsl(var(--brand-teal))' }}>Sync</span>
                </h1>
                <span
                  className="inline-block rounded-full px-2 py-0.5 text-xs font-medium"
                  style={{
                    backgroundColor: 'hsl(var(--brand-navy) / 0.10)',
                    color: 'hsl(var(--brand-navy))',
                  }}
                >
                  {org?.display_name ?? '...'}
                </span>
              </span>
            </Link>
            <PlanBadge />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {user?.display_name}
            </span>
            <button
              type="button"
              onClick={handleSignOut}
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Sign out
            </button>
          </div>
        </div>
        <nav className="container mx-auto flex gap-1 -mt-1 pb-1 flex-wrap" aria-label="Primary">
          <NavTab to="/" end>Home</NavTab>
          <NavTab to="/locations">Locations</NavTab>
          {/* /staff and /staff/:id are owner+manager-only at the route guard
              (App.tsx); without this gate the Staff tab would render for
              staff users but bounce back to Home when clicked. Keep the
              role check here in sync with the RouteGuard in App.tsx. */}
          {(isOwner || isAdmin || role === 'manager') && <NavTab to="/staff">Staff</NavTab>}
          <NavTab to="/schedule">Schedule</NavTab>
          <NavTab to="/time-off">Time off</NavTab>
          <NavTab to="/my-activity">My activity</NavTab>
          {(isOwner || isAdmin || role === 'manager') && <NavTab to="/manager-reports">Reports</NavTab>}
          {(isOwner || isAdmin) && <NavTab to="/owner-reports">Owner reports</NavTab>}
          {(isOwner || isAdmin) && <NavTab to="/audit">Audit</NavTab>}
          {isOwner && <NavTab to="/owner/banners">Comms</NavTab>}
          {isOwner && <NavTab to="/billing">Billing</NavTab>}
          {isAdmin && <NavTab to="/admin">Admin</NavTab>}
        </nav>
      </header>
      <main id="main-content" ref={mainRef} tabIndex={-1}>
        {children}
      </main>
      <footer
        className="container mx-auto py-6 px-4 mt-12 border-t border-border text-xs text-muted-foreground flex flex-wrap gap-4"
        aria-label="Legal links"
      >
        <Link to="/pricing" className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded">Pricing</Link>
        <Link to="/privacy" className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded">Privacy</Link>
        <Link to="/terms" className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded">Terms</Link>
        <Link to="/dpa" className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded">DPA</Link>
      </footer>
    </div>
  );
}

function NavTab({ to, end, children }: { to: string; end?: boolean; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'rounded-md px-3 py-1.5 text-sm hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          isActive
            ? 'text-foreground font-medium border-b-2 border-primary -mb-[1px]'
            : 'text-muted-foreground'
        )
      }
    >
      {children}
    </NavLink>
  );
}